/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useState } from "react";
import { If, Then, Else } from "react-if";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { Box, Button, Loader, Spacer, Text } from "@nordcloud/gnui";
import { DiscoveryRuleDisplay } from "~/components/Environment/DiscoveryRuleDisplay";
import { useShowError } from "~/components/Error/useShowError";
import FlexContainer from "~/components/Styled/FlexContainer";
import { DiscoveryRule } from "~/models";
import { useAxios } from "~/tools";

type RouteParams = {
  customerId: string;
  application: string;
  environmentId: string;
};

const EditLink = styled.a`
  &:hover {
    text-decoration: none;
  }
`;

function DiscoveryRules() {
  const params = useParams<RouteParams>();
  const environmentId = params.environmentId;

  const klarityBaseUrl = process.env.REACT_APP_KLARITY_URL;
  const klarityUrl = `${klarityBaseUrl}/applications/${params.application}/${environmentId}?customerId=${params.customerId}`;

  const [rules, setRules] = useState<DiscoveryRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [noKlarityKey, setNoKlarityKey] = useState(false);

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const fetchDiscoveryRules = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(
        `/environments/${environmentId}/discovery-rules`
      );
      setRules(() => data.discoveryRules);
    } catch (e) {
      if (e.response.status === 400) {
        setNoKlarityKey(true);
      } else {
        showError(e.response);
      }
    }
    setRulesLoading(false);
  }, [
    Axios,
    environmentId,
    setRules,
    setNoKlarityKey,
    showError,
    setRulesLoading,
  ]);

  React.useEffect(() => {
    fetchDiscoveryRules();
  }, [fetchDiscoveryRules]);

  return (
    <Box spacing="spacing04">
      <FlexContainer justify="space-between">
        <Text weight="medium">Discovery rules</Text>

        <EditLink href={klarityUrl} target="_blank">
          <Button icon="externalLink">Edit discovery rules</Button>
        </EditLink>
      </FlexContainer>

      <Spacer height={"1rem"} />

      <If condition={rulesLoading}>
        <Then>
          <Loader inContent />
        </Then>
        <Else>
          {rules.length ? (
            rules.map((rule, i) => (
              <DiscoveryRuleDisplay key={i} rule={rule} idx={i + 1} />
            ))
          ) : (
            <div>
              {noKlarityKey
                ? "Cannot load discovery rules - Overlay does not have Klarity API key for this customer"
                : "No discovery rules"}
            </div>
          )}
        </Else>
      </If>
    </Box>
  );
}

export { DiscoveryRules };
